import { useState, useEffect, useCallback } from "react";
import { Plus, ChevronUp, ChevronDown } from "lucide-react";
import { supabase, MUTED, LINE, SURFACE, text, card, input, textareaStyle, btn, btnSm, iconBtn, linkBtn, errorText, errorBanner, segWrap, segBtn, pageTitle, SHADOW_MODAL, SCRIM, WHITE } from "./constants";
import { CloseButton, ConfirmBar } from "./shared";
import { useIsMobile } from "./lib/responsive";
import CredentialBadge from "./CredentialBadge";

// ─── CREDENTIALS (solo admin, 22/10) ──────────────────────────────────────────
// Credenciales anónimas de recruitment (puesto, sector, fase y un dato) que se
// enseñan como insignias que giran. Tabla `credentials`, RLS solo is_admin().
//
// REGLA: internal_firm e internal_notes son internos. Una futura vista pública
// solo expondrá role, sector, stage, metric_label, metric_value y accent de
// las filas con is_published = true; nunca los internos.
//
// Toda escritura pide .select() y comprueba que hubo fila (RLS = 0 filas, sin
// error). El orden es display_order; se cambia con subir/bajar, sin arrastrar.

const ACCENT_OPTS = [["ink", "Negro", "Black"], ["blue", "Azul", "Blue"], ["green", "Verde", "Green"]];
const BLANK = { role: "", sector: "", stage: "", metric_value: "", metric_label: "", accent: "ink", is_published: false, internal_firm: "", internal_notes: "" };
const FIELDS = ["role", "sector", "stage", "metric_value", "metric_label", "accent", "is_published", "internal_firm", "internal_notes"];

// Listado paginado: Supabase corta en 1.000 filas sin avisar (CLAUDE.md, regla 1)
const fetchAll = async () => {
  const out = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase.from("credentials").select("*")
      .order("display_order", { ascending: true }).order("created_at", { ascending: true })
      .range(from, from + 999);
    if (error) return { error };
    out.push(...(data || []));
    if (!data || data.length < 1000) return { data: out };
  }
};

export default function CredentialsView({ lang }) {
  const es = lang === "es";
  const isMobile = useIsMobile();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [moveErr, setMoveErr] = useState("");
  const [moving, setMoving] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | id

  const load = useCallback(async () => {
    setLoading(true); setLoadErr("");
    const { data, error } = await fetchAll();
    if (error) {
      setLoadErr(es
        ? `No se pudieron cargar las credenciales (${error.message}). Si la tabla credentials aún no existe, falta ejecutar su SQL en Supabase.`
        : `Couldn't load the credentials (${error.message}). If the credentials table doesn't exist yet, its SQL still has to be run in Supabase.`);
    } else setRows(data);
    setLoading(false);
  }, [es]);
  useEffect(() => { load(); }, [load]);

  // Subir/bajar: se reordena la lista y se guarda display_order = posición
  // en las filas que cambian (así los empates del principio se deshacen).
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rows.length || moving) return;
    const next = [...rows];
    [next[idx], next[j]] = [next[j], next[idx]];
    const changed = next.map((r, i) => ({ r, i })).filter(({ r, i }) => r.display_order !== i);
    setMoving(true); setMoveErr("");
    const prev = rows;
    setRows(next.map((r, i) => ({ ...r, display_order: i })));
    const res = await Promise.all(changed.map(({ r, i }) =>
      supabase.from("credentials").update({ display_order: i }).eq("id", r.id).select("id")));
    const bad = res.find(x => x.error || !x.data?.length);
    if (bad) {
      setRows(prev);
      setMoveErr(es ? `No se pudo guardar el orden${bad.error ? ` (${bad.error.message})` : ": la base no dejó escribir"}.` : `Couldn't save the order${bad.error ? ` (${bad.error.message})` : ": the database refused the write"}.`);
    }
    setMoving(false);
  };

  const onSaved = (row, isNew) => {
    setRows(rs => isNew ? [...rs, row] : rs.map(r => r.id === row.id ? row : r));
    setEditing(null);
  };
  const onDeleted = (id) => { setRows(rs => rs.filter(r => r.id !== id)); setEditing(null); };

  const editRow = editing && editing !== "new" ? rows.find(r => r.id === editing) : null;
  const arrow = isMobile ? { width: 40, height: 40 } : null;

  return (
    <div style={{ padding: isMobile ? 16 : 32 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
        <h1 style={{ ...pageTitle(isMobile), flex: 1, minWidth: 0 }}>Credentials</h1>
        <button onClick={() => setEditing("new")} style={{ ...btn.primary, display: "inline-flex", alignItems: "center", gap: 6, ...(isMobile ? { height: 40 } : null) }}>
          <Plus size={16} strokeWidth={1.75} />{es ? "Nueva credencial" : "New credential"}
        </button>
      </div>
      <div style={{ ...text.pageSub, marginBottom: 20 }}>
        {es ? "Solo admin. Credenciales anónimas de búsquedas hechas: el puesto delante, el dato detrás." : "Admin only. Anonymous credentials from past searches: the role on the front, the result on the back."}
      </div>

      {moveErr && <div style={{ ...errorBanner, marginBottom: 16 }}>{moveErr}</div>}
      {loading && <div style={{ ...text.body, color: MUTED }}>{es ? "Cargando..." : "Loading..."}</div>}
      {!loading && loadErr && (
        <div style={{ ...errorBanner, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ flex: 1, minWidth: 200 }}>{loadErr}</span>
          <button onClick={load} style={{ ...btn.ghost, ...btnSm }}>{es ? "Reintentar" : "Retry"}</button>
        </div>
      )}
      {!loading && !loadErr && rows.length === 0 && (
        <div style={{ ...card, textAlign: "center" }}>
          <div style={text.cardTitle}>{es ? "Aún no hay credenciales" : "No credentials yet"}</div>
          <div style={{ ...text.body, color: MUTED, marginTop: 4 }}>{es ? "Crea la primera con «Nueva credencial»." : "Create the first one with “New credential”."}</div>
        </div>
      )}

      {!loading && !loadErr && rows.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 16 }}>
          {rows.map((r, i) => (
            <div key={r.id} style={{ ...card, padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ padding: "8px 0" }}>
                <CredentialBadge credential={r} lang={lang} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4, borderTop: `1px solid ${LINE}`, paddingTop: 10 }}>
                <span style={{ ...text.label, flex: 1 }}>{r.is_published ? (es ? "Publicada" : "Published") : (es ? "Borrador" : "Draft")}</span>
                <button onClick={() => move(i, -1)} disabled={i === 0 || moving} aria-label={es ? "Subir" : "Move up"} title={es ? "Subir" : "Move up"}
                  style={{ ...iconBtn, ...arrow, opacity: i === 0 ? 0.3 : 1, cursor: i === 0 ? "default" : "pointer" }}>
                  <ChevronUp size={18} strokeWidth={1.75} />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === rows.length - 1 || moving} aria-label={es ? "Bajar" : "Move down"} title={es ? "Bajar" : "Move down"}
                  style={{ ...iconBtn, ...arrow, opacity: i === rows.length - 1 ? 0.3 : 1, cursor: i === rows.length - 1 ? "default" : "pointer" }}>
                  <ChevronDown size={18} strokeWidth={1.75} />
                </button>
                <button onClick={() => setEditing(r.id)} style={{ ...linkBtn, marginLeft: 8, ...(isMobile ? { minHeight: 40 } : null) }}>{es ? "Editar" : "Edit"}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CredentialModal key={editing} lang={lang} row={editRow} nextOrder={rows.length ? Math.max(...rows.map(r => r.display_order || 0)) + 1 : 0}
          onClose={() => setEditing(null)} onSaved={onSaved} onDeleted={onDeleted} />
      )}
    </div>
  );
}

function CredentialModal({ lang, row, nextOrder, onClose, onSaved, onDeleted }) {
  const es = lang === "es";
  const isMobile = useIsMobile();
  const isNew = !row;
  const [form, setForm] = useState(() => {
    const f = { ...BLANK };
    if (row) FIELDS.forEach(k => { f[k] = row[k] ?? BLANK[k]; });
    return f;
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErr(""); };

  const missing = ["role", "sector", "stage"].filter(k => !String(form[k] || "").trim());

  const save = async () => {
    if (missing.length) { setErr(es ? "Puesto, sector y fase son obligatorios." : "Role, sector and stage are required."); return; }
    const payload = {};
    FIELDS.forEach(k => { const v = form[k]; payload[k] = typeof v === "string" ? (v.trim() || null) : v; });
    payload.accent = payload.accent || "ink";
    setBusy(true); setErr("");
    const q = isNew
      ? supabase.from("credentials").insert({ ...payload, display_order: nextOrder }).select()
      : supabase.from("credentials").update(payload).eq("id", row.id).select();
    const { data, error } = await q;
    setBusy(false);
    if (error || !data?.length) {
      setErr(es ? `No se pudo guardar${error ? ` (${error.message})` : ": la base no dejó escribir"}.` : `Couldn't save${error ? ` (${error.message})` : ": the database refused the write"}.`);
      return;
    }
    onSaved(data[0], isNew);
  };

  const remove = async () => {
    setBusy(true); setErr("");
    const { data, error } = await supabase.from("credentials").delete().eq("id", row.id).select("id");
    setBusy(false);
    if (error || !data?.length) {
      setConfirmDel(false);
      setErr(es ? `No se pudo borrar${error ? ` (${error.message})` : ": la base no dejó borrar"}.` : `Couldn't delete${error ? ` (${error.message})` : ": the database refused the delete"}.`);
      return;
    }
    onDeleted(row.id);
  };

  const fieldStyle = { ...input, ...(isMobile ? { height: 44, fontSize: 16 } : null) };
  const field = (k, labEs, labEn, required, phEs = "", phEn = "") => (
    <label style={{ display: "block" }}>
      <div style={{ ...text.label, marginBottom: 4 }}>{es ? labEs : labEn}{required ? " *" : ""}</div>
      <input value={form[k] || ""} onChange={e => set(k, e.target.value)} placeholder={es ? phEs : phEn} style={fieldStyle} />
    </label>
  );
  const title = isNew ? (es ? "Nueva credencial" : "New credential") : (es ? "Editar credencial" : "Edit credential");

  const overlay = isMobile
    ? { position: "fixed", inset: 0, background: WHITE, zIndex: 500, display: "flex", flexDirection: "column" }
    : { position: "fixed", inset: 0, background: SCRIM, zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };
  const panel = isMobile
    ? { flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "0 16px calc(24px + env(safe-area-inset-bottom))", boxSizing: "border-box" }
    : { background: WHITE, borderRadius: 16, padding: "28px 32px", width: "100%", maxWidth: 720, maxHeight: "90vh", overflowY: "auto", boxShadow: SHADOW_MODAL, boxSizing: "border-box" };
  const head = isMobile
    ? { position: "sticky", top: 0, zIndex: 1, background: WHITE, margin: "0 -16px 16px", padding: "calc(10px + env(safe-area-inset-top)) 16px 10px", borderBottom: `1px solid ${LINE}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }
    : { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 };

  return (
    <div style={overlay} onClick={isMobile ? undefined : onClose}>
      <div style={panel} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div style={head}>
          <span style={text.cardTitle}>{title}</span>
          <CloseButton onClick={onClose} label={es ? "Cerrar" : "Close"} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1fr) 220px", gap: 24, alignItems: "start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
            {field("role", "Puesto", "Role", true, "p. ej. CFO", "e.g. CFO")}
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12 }}>
              {field("sector", "Sector", "Sector", true, "p. ej. Fintech", "e.g. Fintech")}
              {field("stage", "Fase", "Stage", true, "p. ej. Serie B", "e.g. Series B")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12 }}>
              {field("metric_value", "Dato", "Metric", false, "p. ej. 6 semanas", "e.g. 6 weeks")}
              {field("metric_label", "Qué mide", "Metric label", false, "p. ej. hasta la oferta firmada", "e.g. to signed offer")}
            </div>
            <div>
              <div style={{ ...text.label, marginBottom: 6 }}>{es ? "Acento" : "Accent"}</div>
              <div style={{ ...segWrap, display: "inline-flex" }}>
                {ACCENT_OPTS.map(([v, e1, e2]) => (
                  <button key={v} type="button" onClick={() => set("accent", v)} style={{ ...segBtn(form.accent === v), ...(isMobile ? { height: 40 } : null) }}>{es ? e1 : e2}</button>
                ))}
              </div>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", ...(isMobile ? { minHeight: 40 } : null) }}>
              <input type="checkbox" checked={!!form.is_published} onChange={e => set("is_published", e.target.checked)} style={{ width: 16, height: 16, margin: 0 }} />
              <span style={text.body}>{es ? "Publicada (si no, queda como borrador)" : "Published (otherwise it stays a draft)"}</span>
            </label>

            <div style={{ background: SURFACE, border: `1.5px solid ${LINE}`, borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ ...text.body, fontWeight: 700 }}>{es ? "Los campos internos no se muestran nunca en público." : "Internal fields are never shown publicly."}</div>
              {field("internal_firm", "Empresa (interno)", "Firm (internal)", false)}
              <label style={{ display: "block" }}>
                <div style={{ ...text.label, marginBottom: 4 }}>{es ? "Notas internas" : "Internal notes"}</div>
                <textarea value={form.internal_notes || ""} onChange={e => set("internal_notes", e.target.value)} rows={3}
                  style={{ ...textareaStyle, ...(isMobile ? { fontSize: 16 } : null) }} />
              </label>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, order: isMobile ? -1 : 0 }}>
            <div style={text.label}>{es ? "Vista previa" : "Preview"}</div>
            <CredentialBadge credential={form} lang={lang} />
          </div>
        </div>

        {err && <div style={{ ...errorText, marginTop: 16 }}>{err}</div>}
        {confirmDel ? (
          <ConfirmBar style={{ marginTop: 20 }}
            message={es ? "¿Borrar esta credencial? No se puede deshacer." : "Delete this credential? This can't be undone."}
            actionLabel={es ? "Borrar" : "Delete"} cancelLabel={es ? "Cancelar" : "Cancel"}
            onConfirm={remove} onCancel={() => setConfirmDel(false)} busy={busy} />
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
            {!isNew && (
              <button type="button" onClick={() => setConfirmDel(true)} style={{ ...linkBtn, color: MUTED, ...(isMobile ? { minHeight: 40 } : null) }}>
                {es ? "Borrar credencial" : "Delete credential"}
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button type="button" onClick={onClose} style={{ ...btn.ghost, ...(isMobile ? { height: 44, flex: 1 } : null) }}>{es ? "Cancelar" : "Cancel"}</button>
            <button type="button" onClick={save} disabled={busy}
              style={{ ...(busy ? btn.disabled : btn.primary), ...(isMobile ? { height: 44, flex: 1 } : null) }}>
              {busy ? (es ? "Guardando..." : "Saving...") : (es ? "Guardar" : "Save")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
